import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import { DesktopTimePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import { get, isUndefined } from 'lodash-es';
import { useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
} from 'react-hook-form';

type DayjsTzInput = Parameters<typeof dayjs.tz>[0];

interface NewTimeFormFieldProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  format?: string;
  isDisabled?: boolean;
  name: string;
  size?: 'medium' | 'small';
  sx?: SxProps<Theme>;
}

const DEFAULT_TIME_STEPS = { hours: 1, minutes: 1 };

const NewTimeFormField = <TFieldValues extends FieldValues = FieldValues>({
  control,
  errors,
  format = 'hh:mm a',
  name,
  size = 'medium',
  isDisabled = false,
  sx,
}: NewTimeFormFieldProps<TFieldValues>) => {
  const errorMessage = useMemo(() => {
    return get(errors, `${name}.message`) as string | undefined;
  }, [errors, name]);

  return (
    <Controller
      name={name as FieldPath<TFieldValues>}
      control={control}
      render={({ field: { onChange, value } }) => {
        return (
          <Box sx={[...(Array.isArray(sx) ? sx : [sx])]}>
            <DesktopTimePicker
              // MUI X v8 flipped the desktop time picker's closeOnSelect default to
              // false; keep the v7 close-after-selection behavior.
              closeOnSelect
              enableAccessibleFieldDOMStructure={false}
              timeSteps={DEFAULT_TIME_STEPS}
              format={format}
              onChange={(val) => {
                if (!val || !dayjs.isDayjs(val) || !val.isValid()) {
                  return onChange(null);
                }

                return onChange(val);
              }}
              value={dayjs.tz(value as DayjsTzInput)}
              slotProps={{
                textField: {
                  ...(isUndefined(errorMessage)
                    ? {}
                    : { helperText: errorMessage, error: true }),
                  ...(size === 'small'
                    ? {
                        sx: {
                          '& .MuiOutlinedInput-root': { pr: 0, width: '88px' },
                          '& .MuiOutlinedInput-input': {
                            p: '5px 0 5px 7px',
                            fontSize: '12px',
                          },
                        },
                      }
                    : {}),
                },
                inputAdornment: {
                  ...(size === 'small'
                    ? {
                        sx: {
                          '&.MuiInputAdornment-root': {
                            p: 0,
                            minWidth: '18px',
                            mr: '5px',
                            ml: 0,
                          },
                          '& .MuiIconButton-root': { p: 0, mr: 0 },
                          '& .MuiSvgIcon-root': { width: '.75em', height: '.75em' },
                        },
                      }
                    : {}),
                },
              }}
              disabled={isDisabled}
            />
          </Box>
        );
      }}
    />
  );
};
export { NewTimeFormField };
